import { useEffect, useState } from "react";
import type { LocalVideoTrack, RemoteVideoTrack, RemoteParticipant } from "twilio-video";
import { useVideoCalling } from "./useVideoCallingContext";
import { SCREEN_VIDEO_TRACK_NAME } from "./useScreenShare";

/**
 * The screen feed currently spotlit on the presentation stage.
 */
export interface ActiveScreenShare {
  /** The screen video track to display (local when the current user is sharing, otherwise remote). */
  track: LocalVideoTrack | RemoteVideoTrack;
  /** Whether this is the local user's own screen. */
  isLocal: boolean;
  /** Twilio identity of the remote sharer; null when the local user is sharing. */
  sharerIdentity: string | null;
}

interface RemoteScreen {
  participant: RemoteParticipant;
  track: RemoteVideoTrack;
}

/** Find the first remote participant publishing a subscribed "screen" video track. */
const findRemoteScreen = (participants: RemoteParticipant[]): RemoteScreen | null => {
  for (const participant of participants) {
    for (const publication of participant.tracks.values()) {
      const track = publication.track;
      if (track?.kind === "video" && track.name === SCREEN_VIDEO_TRACK_NAME) {
        return { participant, track };
      }
    }
  }
  return null;
};

/**
 * Returns the screen feed to spotlight on the presentation stage, or null when nobody is sharing.
 *
 * Prefers a remote participant's screen (what a viewer wants to see) and falls back to the local
 * screen (so the person sharing sees their own feed). Stays reactive to participants starting and
 * stopping screen share via Twilio track subscription events.
 */
export const useActiveScreenShare = (): ActiveScreenShare | null => {
  const { participants, localScreenTrack, isScreenSharing } = useVideoCalling();
  const [remoteScreen, setRemoteScreen] = useState<RemoteScreen | null>(null);

  useEffect(() => {
    const update = () => setRemoteScreen(findRemoteScreen(participants));
    update();

    participants.forEach((participant) => {
      participant.on("trackSubscribed", update);
      participant.on("trackUnsubscribed", update);
    });
    return () => {
      participants.forEach((participant) => {
        participant.off("trackSubscribed", update);
        participant.off("trackUnsubscribed", update);
      });
    };
  }, [participants]);

  if (remoteScreen) {
    return { track: remoteScreen.track, isLocal: false, sharerIdentity: remoteScreen.participant.identity };
  }
  if (isScreenSharing && localScreenTrack) {
    return { track: localScreenTrack, isLocal: true, sharerIdentity: null };
  }
  return null;
};
